import React, { useState } from 'react';
import RefLink from '../shared/RefLink';
import { FormModal, Field, inputCls, btnGhost } from '../shared/FormKit';
import CpdFlagsCard from '../workflow/CpdFlagsCard';
import CertificateReadingCard from '../workflow/CertificateReadingCard';
import { formatDate } from '../../utils/dateFormat';
import { fetchJSON } from '../../utils/api';

// HR's spot-check of one External CPD record, from Management → Training
// Records → External CPD (docs/modules/workflow-ld-cpd.md §4). The record
// was filed by a declaration; this shows the declaration's flags (confirm
// each, or all), what the certificate reader saw against what was declared,
// and the Return action — the record is withdrawn and the declarant told why.
//
//   record    — the list row (carries requestId / requestNo / flags / extracted)
//   canReview — the viewer holds ld:cpd:review
//   onChanged — (notice: string) => void after a confirm or a return; the
//               caller reloads the list
const CpdSpotCheckModal = ({ record, canReview, onClose, onChanged }) => {
  const [flags, setFlags] = useState(record.flags || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [returning, setReturning] = useState(false);
  const [reason, setReason] = useState('');

  const confirm = async (codes) => {
    setBusy(true);
    setError(null);
    try {
      const data = await fetchJSON(`/api/ld/cpd-requests/${record.requestId}/flags/confirm`, {
        method: 'POST',
        body: JSON.stringify({ codes }),
      });
      setFlags(data.request.flags || []);
      onChanged(codes.length === 1
        ? `Flag confirmed on ${record.requestNo}.`
        : `All flags confirmed on ${record.requestNo}.`);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const doReturn = async () => {
    if (!reason.trim()) { setError('A reason is required to return a declaration.'); return; }
    setBusy(true);
    setError(null);
    try {
      await fetchJSON(`/api/ld/cpd-requests/${record.requestId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() }),
      });
      onChanged(`${record.requestNo} returned to ${record.staffName} — the record and its hours have been withdrawn.`);
      onClose();
    } catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <FormModal
      title={`Spot-check ${record.requestNo}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Close</button>
          {canReview && !returning && (
            <button
              type="button"
              onClick={() => setReturning(true)}
              disabled={busy}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              Return for Changes
            </button>
          )}
          {canReview && returning && (
            <button
              type="button"
              onClick={doReturn}
              disabled={busy}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {busy ? 'Returning…' : 'Confirm Return'}
            </button>
          )}
        </>
      )}
    >
      <div className="text-sm text-slate-700">
        <p className="font-medium text-slate-900">{record.trainingTitle}</p>
        <p className="text-xs text-slate-500 mt-0.5">
          {record.staffName} — {record.hours}h, certificate dated {formatDate(record.certificateDate)}
          {' — '}
          <RefLink to={`/workflow/cpd/${record.requestId}`} className="text-blue-600 hover:text-blue-800 underline">
            open declaration {record.requestNo}
          </RefLink>
        </p>
      </div>

      <div>
        <p className="text-xs font-medium text-slate-500 mb-1.5">Flags</p>
        {flags.length === 0
          ? <p className="text-sm text-slate-500">Nothing was flagged on this declaration.</p>
          : <CpdFlagsCard flags={flags} canConfirm={canReview} onConfirm={confirm} busy={busy} bare />}
      </div>

      {record.extracted && (
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1.5">What the Certificate Says</p>
          <CertificateReadingCard
            extracted={record.extracted}
            declared={record}
            staffName={record.staffName}
            bare
          />
        </div>
      )}

      {returning && (
        <Field label="Reason for Return" required>
          <textarea
            rows="3"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={inputCls}
            placeholder="What is wrong with this declaration — the declarant reads this."
          />
          <p className="text-xs text-slate-500 mt-1">
            Returning withdraws the record and its hours at once. The declarant is emailed the reason and can
            correct and re-submit, or delete the declaration.
          </p>
        </Field>
      )}

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}
    </FormModal>
  );
};

export default CpdSpotCheckModal;
